import React from 'react';
import { useTheme } from '../ThemeContext';

function formatTime(timestamp) {
  if (!timestamp) return '';
  const date = timestamp?.toDate ? timestamp.toDate() : new Date(timestamp);
  if (isNaN(date.getTime())) return '';
  return date.toLocaleString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true,
  });
}

function ChatBubble({ message, flaggedText, t }) {
  const isFlagged = flaggedText && message.text && message.text.toLowerCase().includes(flaggedText.toLowerCase());
  return (
    <div style={{ marginBottom: 10 }}>
      <div style={{ fontSize: 11, color: '#8E8E93', marginBottom: 3, fontWeight: 500 }}>
        {message.sender} · {message.time}
      </div>
      {message.attachmentURL && message.attachmentType === 'image' && (
        <div style={{ marginBottom: message.text && message.text !== '📷 Photo' ? 6 : 0 }}>
          <img
            src={message.attachmentURL}
            alt="Shared photo"
            onClick={() => window.open(message.attachmentURL, '_blank')}
            style={{
              maxWidth: 200, maxHeight: 200, borderRadius: 12,
              cursor: 'pointer', display: 'block',
              border: '1px solid #E5E7EB',
            }}
          />
        </div>
      )}
      {message.attachmentURL && message.attachmentType === 'document' && (
        <div style={{ marginBottom: message.text ? 6 : 0 }}>
          
            <a href={message.attachmentURL}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              padding: '8px 12px', borderRadius: 10,
              background: '#EEF2FF', color: '#3B7DD8',
              fontSize: 13, fontWeight: 600, textDecoration: 'none',
              border: '1px solid #C7D2FE',
            }}
          >
            📎 {message.attachmentName || 'Document'}
          </a>
        </div>
      )}
      {message.text && message.text !== '📷 Photo' && message.text !== `📎 ${message.attachmentName}` && (
        <div style={{
          display: 'inline-block', padding: '10px 14px', borderRadius: 18,
          background: isFlagged ? (t.chatBubble === '#1C2128' ? 'rgba(214,48,49,0.15)' : 'linear-gradient(135deg, #FFE8E8 0%, #FFF0F0 100%)') : t.chatBubble,
          border: isFlagged ? '1.5px solid #FFB8B8' : `1px solid ${t.chatBubbleBorder}`,
          fontSize: 14, color: t.text, maxWidth: '85%', lineHeight: 1.45,
        }}>
          {message.text}
          {isFlagged && (
            <span style={{
              display: 'inline-block', marginLeft: 8, fontSize: 10, color: '#D63031',
              fontWeight: 700, background: '#FFD4D4', padding: '2px 6px', borderRadius: 6,
              verticalAlign: 'middle',
            }}>
              FLAGGED
            </span>
          )}
        </div>
      )}
    </div>
  );
}

function DetailPanel({ item, type, onClose, onMarkReviewed, onDelete }) {
  if (!item) return null;
  const { t } = useTheme();

  const isReport = type === 'report';
  const flaggedText = !isReport ? item.matchedText : null;

  return (
    <>
      <div
        onClick={onClose}
        style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: t.overlay, zIndex: 90,
        }}
      />
      <div style={{
        position: 'fixed', top: 0, right: 0, bottom: 0,
        width: 480, maxWidth: '100vw', background: t.panel,
        boxShadow: '-8px 0 40px rgba(0,0,0,0.08)', zIndex: 100,
        display: 'flex', flexDirection: 'column',
        fontFamily: "'SF Pro Display', -apple-system, BlinkMacSystemFont, sans-serif",
        animation: 'slideIn 0.25s ease-out',
      }}>
        <style>{`@keyframes slideIn { from { transform: translateX(100%); } to { transform: translateX(0); } }`}</style>

        {/* Header */}
        <div style={{
          padding: '20px 24px', borderBottom: `1px solid ${t.panelBorder}`,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          <div>
            <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: t.text }}>
              {isReport ? 'Report Details' : 'Flagged Chat Details'}
            </h3>
            <p style={{ margin: '4px 0 0', fontSize: 12, color: '#8E8E93' }}>
              {formatTime(item.timestamp)}
            </p>
          </div>
        </div>

        {/* Content */}
        <div style={{ flex: 1, overflow: 'auto', padding: 24 }}>
          {isReport ? (
            <>
              <div style={{
                background: t.sectionBg, borderRadius: 14, padding: 16, marginBottom: 12,
                border: `1px solid ${t.sectionBorder}`,
              }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: '#8E8E93', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 8 }}>
                  Reported By
                </div>
                <div style={{ fontSize: 15, fontWeight: 600, color: t.text }}>{item.reportedBy.name}</div>
                <div style={{ fontSize: 13, color: '#8E8E93' }}>{item.reportedBy.email}</div>
                <div style={{ fontSize: 12, color: '#3B7DD8', fontWeight: 500, marginTop: 2 }}>{item.reportedBy.major}</div>
              </div>
              <div style={{
                background: t.sectionBg, borderRadius: 14, padding: 16, marginBottom: 12,
                border: `1px solid ${t.sectionBorder}`,
              }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: '#8E8E93', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 8 }}>
                  Reported User
                </div>
                <div style={{ fontSize: 15, fontWeight: 600, color: t.text }}>{item.reportedUser.name}</div>
                <div style={{ fontSize: 13, color: '#8E8E93' }}>{item.reportedUser.email}</div>
                <div style={{ fontSize: 12, color: '#E67E22', fontWeight: 500, marginTop: 2 }}>{item.reportedUser.major}</div>
              </div>
              <div style={{
                background: t.sectionBg, borderRadius: 14, padding: 16, marginBottom: 20,
              }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: '#8E8E93', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 6 }}>
                  Reason
                </div>
                <div style={{ fontSize: 15, fontWeight: 600, color: t.text }}>{item.reason}</div>
              </div>
            </>
          ) : (
            <>
              <div style={{
                background: t.sectionBg, borderRadius: 14, padding: 16, marginBottom: 12,
                border: `1px solid ${t.sectionBorder}`,
              }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: '#8E8E93', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 8 }}>
                  Detected Keyword
                </div>
                <div style={{
                  display: 'inline-block', background: '#D63031', color: '#fff',
                  padding: '4px 12px', borderRadius: 8, fontSize: 14, fontWeight: 700,
                  fontFamily: "'SF Mono', 'Fira Code', monospace",
                }}>
                  "{item.keyword}"
                </div>
              </div>
              <div style={{
                background: t.sectionBg, borderRadius: 14, padding: 16, marginBottom: 12,
                border: `1px solid ${t.sectionBorder}`,
              }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: '#8E8E93', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 8 }}>
                  User
                </div>
                <div style={{ fontSize: 15, fontWeight: 600, color: t.text }}>{item.user.name}</div>
                <div style={{ fontSize: 13, color: '#8E8E93' }}>{item.user.email}</div>
                <div style={{ fontSize: 12, color: '#3B7DD8', fontWeight: 500, marginTop: 2 }}>{item.user.major}</div>
              </div>
              <div style={{
                background: t.sectionBg, borderRadius: 14, padding: 16, marginBottom: 20,
                border: `1px solid ${t.sectionBorder}`,
              }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: '#8E8E93', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 6 }}>
                  Matched Message
                </div>
                <div style={{ fontSize: 14, color: '#D63031', fontWeight: 500, fontStyle: 'italic', lineHeight: 1.5 }}>
                  "{item.matchedText}"
                </div>
              </div>
            </>
          )}

          {/* Chat Context */}
          <div style={{ marginBottom: 20 }}>
            <div style={{
              fontSize: 11, fontWeight: 600, color: '#8E8E93', textTransform: 'uppercase',
              letterSpacing: '0.5px', marginBottom: 12,
            }}>
              Chat Context
            </div>
            <div style={{
              background: t.sectionBg, borderRadius: 16, padding: 16,
              border: `1px solid ${t.panelBorder}`,
            }}>
              {item.chatContext.map((msg, i) => (
                <ChatBubble key={i} message={msg} flaggedText={flaggedText} t={t} />
              ))}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div style={{
          padding: '16px 24px', borderTop: `1px solid ${t.panelBorder}`,
          display: 'flex', flexDirection: 'column', gap: 10,
        }}>
          <div style={{ display: 'flex', gap: 10 }}>
            {(item.status === 'unreviewed' || item.status === 'pending') && (
              <button
                onClick={() => onMarkReviewed(item.id, type)}
                style={{
                  flex: 1, padding: 13, borderRadius: 14, border: 'none',
                  background: 'linear-gradient(135deg, #27AE60 0%, #219A52 100%)',
                  color: '#fff', fontSize: 14, fontWeight: 600, cursor: 'pointer',
                  fontFamily: 'inherit', boxShadow: '0 4px 12px rgba(39,174,96,0.25)',
                }}
              >
                Mark as Reviewed ✓
              </button>
            )}
            <button
              onClick={onClose}
              style={{
                flex: (item.status === 'unreviewed' || item.status === 'pending') ? 0 : 1,
                minWidth: (item.status === 'unreviewed' || item.status === 'pending') ? 100 : undefined,
                padding: 13, borderRadius: 14,
                border: `1.5px solid ${t.closeBorder}`, background: t.closeBg,
                color: t.closeText, fontSize: 14, fontWeight: 600, cursor: 'pointer',
                fontFamily: 'inherit',
              }}
            >
              Close
            </button>
          </div>
          {item.status === 'reviewed' && (
            <button
              onClick={() => {
                if (window.confirm('Move this to deleted? You can still view it in the Deleted tab.')) {
                  onDelete(item.id, type);
                }
              }}
              style={{
                width: '100%', padding: 13, borderRadius: 14, border: 'none',
                background: t.deleteBg, color: t.deleteText,
                fontSize: 14, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
              }}
            >
              Delete
            </button>
          )}
        </div>
      </div>
    </>
  );
}

export default DetailPanel;